// ─────────────────────────────────────────────────────────────────────────────
// INVOICE MONEY, THE ONE RULE (item 34): PER LINE. The browser's copy of
// "Aidey Backend/lib/invoiceAmounts.js" (which the PDF, the public page, the portal, the charge and
// Xero use), deliberately, for the editor's live totals and the Tax summary. Pinned to
// "Aidey Backend/ci/invoice_amount_cases.json" by invoiceAmounts.test.js; the rule is written out
// in that table and in the backend file.
// ─────────────────────────────────────────────────────────────────────────────
import { lineRate, RATE_ORDER } from './vatRates';

export const ZERO_DECIMAL = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF',
  'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

function digitsFor(currency) {
  return ZERO_DECIMAL.has(String(currency || '').toUpperCase()) ? 0 : 2;
}
// Half away from zero, to the minor unit. toPrecision(15) first, so 2.675 (stored as
// 2.67499999999999982236431605997495353221893310546875) is treated as the 2.675 a person typed.
export function roundMinor(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return 0;
  const f = Math.pow(10, digitsFor(currency));
  const r = Math.round(Number((Math.abs(n) * f).toPrecision(15))) / f;
  return n < 0 ? -r : r;
}

export function lineAmounts(line, currency) {
  const l = line && typeof line === 'object' ? line : {};
  const qty = Number(l.qty) || 0;
  const unit = Number(l.unitPrice) || 0;
  const rate = lineRate(l);
  let net, vat;
  if (l.vatIncluded === true) {
    const gross = roundMinor(qty * unit, currency);
    vat = roundMinor(gross * rate.percent / (100 + rate.percent), currency);
    net = roundMinor(gross - vat, currency);
  } else {
    net = roundMinor(qty * unit, currency);
    vat = roundMinor(net * rate.percent / 100, currency);
  }
  const unitNet = l.vatIncluded === true ? (qty ? roundMinor(net / qty, currency) : 0) : unit;
  return { qty, unitNet, net, vat, gross: roundMinor(net + vat, currency), rate };
}

function rateKey(rate) { return rate.type || `pct:${rate.percent}`; }

export function invoiceAmounts(inv) {
  const currency = inv?.currency || 'GBP';
  const items = Array.isArray(inv?.lineItems) ? inv.lineItems : [];
  const lines = items.map((r) => lineAmounts(r, currency));
  const groups = new Map();
  for (const l of lines) {
    const key = rateKey(l.rate);
    const g = groups.get(key) || { key, type: l.rate.type, percent: l.rate.percent, label: l.rate.label, note: l.rate.note, net: 0, vat: 0 };
    g.net = roundMinor(g.net + l.net, currency);
    g.vat = roundMinor(g.vat + l.vat, currency);
    groups.set(key, g);
  }
  const order = (g) => (g.type ? RATE_ORDER.indexOf(g.type) : RATE_ORDER.length);
  const byRate = [...groups.values()].sort((a, b) => order(a) - order(b) || b.percent - a.percent);
  const subtotal = roundMinor(lines.reduce((s, l) => s + l.net, 0), currency);
  const tax = roundMinor(lines.reduce((s, l) => s + l.vat, 0), currency);
  return { subtotal, tax, total: roundMinor(subtotal + tax, currency), currency, lines, byRate };
}
